import { useAuthStore } from '../store/authStore';

const SESSION_EXPIRED_MESSAGE = 'Tu sesión expiró. Inicia sesión nuevamente.';

export class UnauthorizedError extends Error {
  constructor(message = SESSION_EXPIRED_MESSAGE) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

const extractErrorMessage = (payload: unknown, fallback: string) => {
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if (typeof record.error === 'string' && record.error.trim()) return record.error;
    if (typeof record.message === 'string' && record.message.trim()) return record.message;
  }
  return fallback;
};

const parseBody = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }
  const text = await response.text().catch(() => '');
  return text || null;
};

const forceLogout = async () => {
  try {
    await useAuthStore.getState().logout();
  } catch {
    useAuthStore.setState({ session: null, user: null, initialized: true });
  }
};

export const isUnauthorizedError = (error: unknown): error is UnauthorizedError =>
  error instanceof UnauthorizedError;

export async function fetchJsonWithAuth<T = unknown>(input: RequestInfo | URL, init: RequestInit = {}) {
  const token = useAuthStore.getState().session?.access_token;

  if (!token) {
    await forceLogout();
    throw new UnauthorizedError();
  }

  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(input, { ...init, headers });
  const payload = await parseBody(response);

  if (response.status === 401) {
    await forceLogout();
    throw new UnauthorizedError(extractErrorMessage(payload, SESSION_EXPIRED_MESSAGE));
  }

  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, `Error ${response.status}`));
  }

  return payload as T;
}
